// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AddRounded } from '@/constants/hardfin-standard-icons';
import { getContacts } from '@/contact/api/get-contacts';
import { Contact } from '@/contact/api/types';
import { CreateContactDialog } from '@/contact/components/create-contact-dialog';
import { useDisclosure } from '@/hooks/use-disclosure';
import { SparseCustomer } from '@/sparse/api/types';
import {
  Autocomplete,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { ForwardedRef, forwardRef, useMemo, useState } from 'react';

const CREATE_CONTACT_OPTION_ID = 'create-contact';

interface ContactSearchProps {
  value: Contact | null;
  onChange: (value: Contact | null) => void;
  label: string;
  name?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  priorityCustomerId?: string;
  defaultCustomer?: SparseCustomer | null;
  size?: 'small' | 'medium';
}

function formatContactSubline(contact: Contact): string {
  const parts: string[] = [];
  if (contact.email) {
    parts.push(contact.email);
  }
  if (contact.phone) {
    parts.push(contact.phone);
  }
  return parts.join(' | ');
}

function ContactSearchInner(
  props: ContactSearchProps,
  ref: ForwardedRef<HTMLInputElement>
) {
  const createDialog = useDisclosure();
  const [createName, setCreateName] = useState('');
  const [inputValue, setInputValue] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['contact', { isArchived: false }],
    queryFn: () =>
      getContacts({
        filter: { isArchived: false },
        limit: 500,
        sort: { property: 'name', direction: 'asc' },
      }),
  });

  const sortedContacts = useMemo(() => {
    const contacts = data?.contacts ?? [];

    if (!props.priorityCustomerId) {
      return contacts;
    }

    const priority: Contact[] = [];
    const rest: Contact[] = [];

    for (const contact of contacts) {
      if (contact.customer.id === props.priorityCustomerId) {
        priority.push(contact);
      } else {
        rest.push(contact);
      }
    }

    return [...priority, ...rest];
  }, [data?.contacts, props.priorityCustomerId]);

  const groupBy = useMemo(() => {
    if (!props.priorityCustomerId) {
      return undefined;
    }
    return (option: Contact): string => {
      if (option.id === CREATE_CONTACT_OPTION_ID) {
        return '';
      }
      if (option.customer.id === props.priorityCustomerId) {
        return option.customer.name;
      }
      return 'Other contacts';
    };
  }, [props.priorityCustomerId]);

  const handleCreateSuccess = (contact: Contact) => {
    props.onChange(contact);
  };

  return (
    <>
      <Autocomplete
        ref={ref}
        size={props.size}
        value={props.value}
        disabled={props.disabled}
        options={sortedContacts}
        loading={isLoading}
        groupBy={groupBy}
        getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        inputValue={inputValue}
        onInputChange={(_event, value, reason) => {
          if (reason === 'input') {
            setInputValue(value);
          } else if (reason === 'reset') {
            setInputValue(value);
          } else if (reason === 'clear') {
            setInputValue('');
          }
        }}
        onChange={(_event, value) => {
          if (value && value.id === CREATE_CONTACT_OPTION_ID) {
            setCreateName(inputValue.trim());
            setInputValue(
              props.value
                ? `${props.value.firstName} ${props.value.lastName}`
                : ''
            );
            props.onChange(props.value);
            if (document.activeElement instanceof HTMLElement) {
              document.activeElement.blur();
            }
            createDialog.open();
            return;
          }
          props.onChange(value);
        }}
        filterOptions={(options, state) => {
          const input = state.inputValue.toLowerCase();
          const filtered = input
            ? options.filter((option) => {
                const name =
                  `${option.firstName} ${option.lastName}`.toLowerCase();
                const email = option.email?.toLowerCase() ?? '';
                const phone = option.phone?.toLowerCase() ?? '';
                return (
                  name.includes(input) ||
                  email.includes(input) ||
                  phone.includes(input)
                );
              })
            : options;

          // Always append the "Create new contact" option
          const createOption = {
            id: CREATE_CONTACT_OPTION_ID,
            key: CREATE_CONTACT_OPTION_ID,
            firstName: '',
            lastName: '',
          } as Contact;

          return [...filtered, createOption];
        }}
        renderOption={(optionProps, option) => {
          if (option.id === CREATE_CONTACT_OPTION_ID) {
            const trimmed = inputValue.trim();
            const label = trimmed
              ? `Add contact ${trimmed}`
              : 'Add new contact';
            return (
              <li {...optionProps} key={option.id}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <AddRounded fontSize="small" color="primary" />
                  <Typography color="primary.main">{label}</Typography>
                </Stack>
              </li>
            );
          }

          const subline = formatContactSubline(option);
          return (
            <li {...optionProps} key={option.id}>
              <Stack>
                <Typography variant="body2">
                  {option.firstName} {option.lastName}
                </Typography>
                {subline && (
                  <Typography variant="caption" color="text.secondary">
                    {subline}
                  </Typography>
                )}
              </Stack>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            name={props.name}
            label={props.label}
            error={props.error}
            helperText={props.helperText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      <CreateContactDialog
        isOpen={createDialog.isOpen}
        onClose={createDialog.close}
        onCreated={handleCreateSuccess}
        defaultCustomer={props.defaultCustomer}
        defaultName={createName}
      />
    </>
  );
}

export const ContactSearch = forwardRef(ContactSearchInner);
